export const VIDEO_COMPARISON_TERMINAL = new Set([
  'completed', 'failed', 'cancelled', 'canceled', 'error',
]);

/**
 * Where the 3D tile lands, so the browser can render it at exactly that size.
 *
 * Mirrors `_layout_cells` in VideoTrackRender's worker/comparison.py — the two
 * have to agree or the tile arrives at the wrong aspect and gets letterboxed
 * inside its own cell. Capturing to the destination size also means the server
 * never resamples it, and the render is quicker for being smaller.
 *
 *   1 video + 3D  -> two 960x1080 halves      (side by side)
 *   2-3 videos+3D -> a 2x2 grid of 960x540
 *   4 videos + 3D -> a 3x2 grid of 640x540
 */
export function comparison3dCellSize(videoCount, width = 1920, height = 1080) {
  if (videoCount <= 1) return { width: width / 2, height };
  if (videoCount >= 4) return { width: Math.round(width / 3 / 2) * 2, height: height / 2 };
  return { width: width / 2, height: height / 2 };
}

export const COMPARISON_3D_MAX_LAPS = 4;

/**
 * Which laps the ghost-race tile races.
 *
 * `videoLaps` are the laps the video panes are showing, in pane order - they
 * go first because the export's refIndex addresses REF by that order and the
 * tile is positioned by that car. The rest fills from `panelLaps`, whatever the
 * 3D panel is racing (every loaded driver's ticked laps through
 * pickLap3dLaps), so a driver loaded without video still appears in the tile:
 * the panel shows them, and the export should not silently drop them. Without
 * a panel set it falls back to `ownLaps`, the video driver's other ticked laps.
 *
 * Entries are `{ session_id, lap_index, ... }`; duplicates are dropped by that
 * pair, and the result is capped at the 3D view's four cars.
 */
export function comparison3dTileLaps(videoLaps, panelLaps = [], ownLaps = []) {
  const keyOf = lap => `${lap.session_id}:${lap.lap_index}`;
  const laps = [...videoLaps];
  const seen = new Set(laps.map(keyOf));
  const fill = panelLaps.length ? panelLaps : ownLaps;
  for (const lap of fill) {
    if (laps.length >= COMPARISON_3D_MAX_LAPS) break;
    if (seen.has(keyOf(lap))) continue;
    seen.add(keyOf(lap));
    laps.push(lap);
  }
  return laps;
}

export function buildVideoComparisonExportRequest({
  drivers,
  referenceDriver,
  playbackMode,
  preSeconds,
  postSeconds,
  threeDClipId = null,
}) {
  return {
    sources: drivers.map(driver => ({
      session_id: driver.sessionId,
      lap_index: driver.lapIndex,
      video_offset_sec: driver.videoOffset,
    })),
    reference_driver: referenceDriver,
    playback_mode: playbackMode,
    pre_seconds: preSeconds,
    post_seconds: postSeconds,
    // Omitted rather than sent as null: the backend treats absence as "no 3D
    // tile", and an explicit null would fail its uuid pattern.
    ...(threeDClipId ? { three_d_clip_id: threeDClipId } : {}),
  };
}
